import { useEffect, useState } from 'react';
import { CalendarClock, Loader2, Upload } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { TurnosInstalacaoSeletor } from '@/components/pedido/inline/AgendaTurnoInline';
import { valorDoTurno, type TurnoInstalacao } from '@/lib/turnoInstalacao';

export interface PedidoComFibra {
  pedido_id: string;
  razao_social: string;
  itens: { produto: string; numero_cotacao_operadora?: string | null }[];
}

/** A mesma janela marca a data nas três portas da fibra (maquete v7, Douglas 30/10/2026). */
export type PortaDaDataFibra = 'inputar' | 'nova_data' | 'nova_data_cliente' | 'retomar';

/** A conversa que libera a nova data pelo cliente: a fala da BKO e a resposta dele. */
export interface ConversaNovaData {
  bkoEm: string | null;
  bkoTexto: string | null;
  clienteEm: string;
  clienteTexto: string;
}

const MOTIVOS_DO_CLIENTE = ['O cliente pediu outra data', 'O cliente não vai estar no endereço'];

const TEXTOS: Record<PortaDaDataFibra, { titulo: string; rotulo: string; nota: string; botao: string }> = {
  inputar: {
    titulo: 'Inputar a fibra',
    rotulo: 'Data da instalação que está no Conexão',
    nota: 'A TV, o Mesh e o ponto adicional do pedido levam esta data. Depois do input, ela muda só pela Nova data da Triagem Em instalação: quando o cliente responde ou depois do Técnico não veio.',
    botao: 'Inputar e mandar para Em instalação',
  },
  nova_data: {
    titulo: 'Nova data da Claro',
    rotulo: 'Nova data da instalação que a Claro marcou',
    nota: 'O cliente recebe a nova data e o relógio do turno recomeça. A data antiga fica na timeline do pedido.',
    botao: 'Lançar a nova data',
  },
  nova_data_cliente: {
    titulo: 'Nova data · o cliente pediu',
    rotulo: 'Nova data da instalação que o cliente pediu',
    nota: 'A conversa vai para a timeline do pedido como prova. O cliente recebe a nova data e o relógio do turno recomeça. Trocar a data não destrava.',
    botao: 'Lançar a nova data',
  },
  retomar: {
    titulo: 'Retomar a fibra',
    rotulo: 'Nova data da instalação que a Claro marcou',
    nota: 'A fibra volta para a Em instalação com esta data: o cliente recebe o aviso e o relógio do turno recomeça.',
    botao: 'Retomar com a data nova',
  },
};

interface InputarFibraDialogProps {
  pedido: PedidoComFibra | null;
  isLoading: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (agenda: string, motivo?: string) => void;
  porta?: PortaDaDataFibra;
  conversa?: ConversaNovaData | null;
}

function hojeEmSaoPaulo(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo' }).format(new Date());
}

function quando(iso: string | null): string {
  if (!iso) return '';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso)).replace(',', '');
}

/**
 * QUEM INPUTA MARCA A DATA OFICIAL DA INSTALAÇÃO (Douglas 29–30/10/2026, maquete
 * aprovada). O campo começa vazio de propósito: o BKO digita a data e o turno que
 * estão no Conexão, nunca confirma uma data escrita antes. A fixa vai direto para
 * a Em instalação; a trava mora em fn_pedido_inputado.
 */
export function InputarFibraDialog({ pedido, isLoading, onOpenChange, onConfirm, porta = 'inputar', conversa }: InputarFibraDialogProps) {
  const t = TEXTOS[porta];
  const Icone = porta === 'inputar' ? Upload : CalendarClock;
  const [data, setData] = useState('');
  const [turno, setTurno] = useState<TurnoInstalacao | null>(null);
  const [motivo, setMotivo] = useState<string | null>(null);
  const peloCliente = porta === 'nova_data_cliente';

  useEffect(() => {
    setData('');
    setTurno(null);
    setMotivo(null);
  }, [pedido?.pedido_id]);

  const hoje = hojeEmSaoPaulo();
  const dataValida = !!data && data >= hoje;
  const podeConfirmar = dataValida && !!turno && (!peloCliente || !!motivo) && !isLoading;
  const produtos = [...new Set((pedido?.itens ?? []).map((i) => i.produto).filter(Boolean))].join(', ');
  const contrato = (pedido?.itens ?? []).map((i) => String(i.numero_cotacao_operadora ?? '').trim()).find(Boolean);

  return (
    <Dialog open={!!pedido} onOpenChange={(o) => !isLoading && onOpenChange(o)}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icone className="h-5 w-5 text-primary" />
            {t.titulo}
          </DialogTitle>
          <DialogDescription>
            {pedido?.razao_social}
            <br />
            {[produtos, contrato ? `Contrato ${contrato}` : null].filter(Boolean).join(' · ')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          {peloCliente && conversa && (
            <div className="space-y-1.5 rounded-[7px] border border-border bg-muted/40 p-2.5 text-[11px]">
              <p className="font-semibold text-foreground">A conversa que libera a nova data</p>
              {conversa.bkoTexto && (
                <p className="text-muted-foreground">
                  <b className="text-foreground">BKO · {quando(conversa.bkoEm)}</b> {conversa.bkoTexto}
                </p>
              )}
              <p className="text-muted-foreground">
                <b className="text-success">Cliente · {quando(conversa.clienteEm)}</b> {conversa.clienteTexto}
              </p>
            </div>
          )}
          {peloCliente && (
            <div className="space-y-1">
              <Label className="text-xs font-semibold">Motivo</Label>
              <div className="flex flex-wrap gap-1.5">
                {MOTIVOS_DO_CLIENTE.map((m) => (
                  <button
                    key={m}
                    type="button"
                    disabled={isLoading}
                    onClick={() => setMotivo(m)}
                    className={cn(
                      'h-7 rounded-[7px] border px-2.5 text-[11px] transition-colors',
                      motivo === m
                        ? 'border-primary/60 bg-primary/10 text-primary'
                        : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-primary',
                    )}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="inputar-fibra-data" className="text-xs font-semibold">
              {t.rotulo}
            </Label>
            <Input
              id="inputar-fibra-data"
              type="date"
              min={hoje}
              value={data}
              disabled={isLoading}
              onChange={(e) => setData(e.target.value)}
            />
            {data && !dataValida && (
              <p className="text-[11px] text-destructive">A data da instalação não pode ser antes de hoje.</p>
            )}
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold">Turno</Label>
            <TurnosInstalacaoSeletor turno={turno} disabled={isLoading} onEscolher={setTurno} />
          </div>
          <p className="text-[11px] text-muted-foreground">{t.nota}</p>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Voltar
          </Button>
          <Button
            className="flex-1 bg-primary hover:bg-primary text-white"
            disabled={!podeConfirmar}
            onClick={() => turno && onConfirm(valorDoTurno(data, turno), peloCliente ? motivo ?? undefined : undefined)}
          >
            {isLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Icone className="h-4 w-4 mr-2" />}
            {t.botao}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
